import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faCheck, faCopy, faEdit, faIdBadge, faMoneyBillTransfer, faTrash, faUsers, faXmark } from "@fortawesome/free-solid-svg-icons";
import { TenantApi, TenantDataType } from "@/api/TenantApi";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";
import moment from "moment";
import LoadingScreen from "@components/content/LoadingScreen";
import { activeOnvoSuscription, deactiveOnvoSuscription } from "@/api/OnvoPayApi";
import ConfirmModal from "@components/content/ConfirmModal";
import { ApiClient } from "@/api/ApiClient";

const TenantIndex = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [tenantToDelete, setTenantToDelete] = useState<TenantDataType | null>(null);
  const modelRef = useRef<ModelIndexHandle>(null);

  useEffect(() => {
  }, []);

  const updateTenantStatus = async (id: string, status: "active" | "paused") => {
    try {
      setIsLoading(true);
      await TenantApi.update(id, {
        status: status
      });
      showAlert('success', 'La suscripcion fue actualizada correctamente');
    } catch (err) {
      showAlert('danger', 'La suscripcion no puedo ser actualizada');
    } finally {
      setIsLoading(false);
    }
    refreshList()
  }

  const refreshList = () => {
    setTimeout(() => {
      modelRef.current?.refetch();
    }, 300);
  }

  const activeOnvo = async (tenantData: TenantDataType) => {
    try {
      setIsLoading(true);
      const isActivate = await activeOnvoSuscription(tenantData)
      if (isActivate) {
        showAlert("success", "La suscripcion ONVO se ha activado correctamente")
        refreshList()
      }
    } catch (err) {
      showAlert("danger", "No fue posible Activar la suscripcion de ONVO")
    } finally {
      setIsLoading(false);
    }
  }

  const deleteTenant = async () => {
    if (!tenantToDelete?.owner || !tenantToDelete?.suscriptionId) return;
    try {
      setIsLoading(true);
      const { data, errors } = await ApiClient.mutations.deleteTenantDataMutation({
        tenantId: tenantToDelete.owner,
        suscriptionId: tenantToDelete.suscriptionId,
      });
      if (errors?.length || !data?.ok) {
        showAlert("danger", data?.error || "No fue posible eliminar la suscripcion");
      } else {
        showAlert("success", `Suscripcion eliminada. ${data.deletedCount ?? 0} registros eliminados.`);
        refreshList();
      }
    } catch {
      showAlert("danger", "Error al eliminar la suscripcion");
    } finally {
      setIsLoading(false);
      setTenantToDelete(null);
    }
  };

  const deactiveOnvo = async (tenantData: TenantDataType) => {
    try {
      setIsLoading(true);
      const isActivate = await deactiveOnvoSuscription(tenantData)
      if (isActivate) {
        refreshList()
        showAlert("success", "La suscripcion ONVO se ha desactivado correctamente")
      }
    } catch (err) {
      showAlert("danger", "No fue posible Cancelar la suscripcion de ONVO")
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <PageContainer
      title="Lista Suscripciones"
      backUrl="/user/dashboard"
      primaryActions={[
        {
          onClick: () => {
            navigate('/admin/suscriptions/add')
          },
          actionTitle: "Nuevo",
          color: "success"
        }
      ]}
      secondaryActions={[
        {
          actionTitle: "Tipos de Suscription",
          href: "/admin/suscription-types"
        }
      ]}
    >
      <ModelIndex<TenantDataType>
        ref={modelRef}
        messages={{
          noResults: "No hay Suscripciones creadas aun"
        }}
        fetchData={(...args) => TenantApi.fetchAllByListNoTenant(...args)}
        filterProps={[
          {
            key: "nombre",
            filterType: "eq"
          }
        ]}
        primaryProperties={[
          {
            key: "numero",
            highlighted: true,
            optionalHighlighted: (val: any, data: TenantDataType) => {
              if (data.status == "toCancel" || data.status == "pendingPaymentMethod") {
                return "pending"
              }
              return data.status != "active"
            },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#1A7E9E",
                "text": "#fff"
              },
              "pending": {
                "bg": "#eaf900",
                "text": "#000"
              }
            },
            className: styles.sequence
          },
          {
            key: (d) => d.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "3" },
            { sm: "10", xs: "9" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.accessUntil, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Acceso Hasta",
          },
          {
            key: (data) => {
              const date = moment(data.currentPeriodEnd, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Periodo Onvo",
          },
          {
            key: (data) => data.status,
            title: "Estado"
          },
          // {
          //   key: (data) => {
          //     return formatMoneyValue(Number(data.monto || 0), data.currency == "CRC" ? 1 : 2)
          //   },
          //   title: "Total",
          //   highlighted: true
          // }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "2" },
            { sm: "4", md: "2" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            href: "/admin/suscriptions/copy/{:id}",
            actionTitle: "Copiar Suscripcion",
            iconProps: { icon: faCopy }
          },
          {
            onClick: (id: string, tenant: TenantDataType) => {
              navigate(`/user/profile/${tenant.suscriptionId}`)
            },
            actionTitle: "Editar Datos de Suscripcion",
            iconProps: { icon: faIdBadge }
          },
          {
            href: "/admin/suscriptions/edit/{:id}",
            actionTitle: "Editar Suscripcion",
            iconProps: { icon: faEdit }
          },
          {
            onClick: (id: string) => {
              navigate(`/admin/suscriptions/${id}/users`)
            },
            actionTitle: "Ver Usuarios",
            iconProps: { icon: faUsers }
          },
          {
            isVisible: (item: TenantDataType) => item.status == "active",
            actionTitle: "Pausar Suscription",
            iconProps: { icon: faXmark, className: styles.cancelBtn },
            needConfirm: true,
            confirmProps: {
              title: "Pausar Suscription",
              confirmMessage: "¿Seguro que desea pausar este Suscripcion?",
              confirmBtnText: "Pausar"
            },
            onClick: (id: string, item: TenantDataType) => { updateTenantStatus(id, "paused") }
          },
          {
            isVisible: (item: TenantDataType) => item.status != "active",
            actionTitle: "Activar Suscription",
            iconProps: { icon: faCheck, className: styles.activeBtn },
            needConfirm: true,
            confirmProps: {
              title: "Activar Suscription",
              confirmMessage: "¿Seguro que desea activar este Suscripcion?",
              confirmBtnText: "Activar"
            },
            onClick: (id: string, item: TenantDataType) => { updateTenantStatus(id, "active") }
          },
          {
            isVisible: (item: TenantDataType) => !item.onvoSubscriptionId,
            actionTitle: "Activar Suscripcion Onvo",
            iconProps: { icon: faMoneyBillTransfer, className: styles.cancelBtn },
            needConfirm: true,
            confirmProps: {
              title: "Activar Suscription Onvo",
              confirmMessage: "¿Seguro que desea activar la suscripcion recurrente de ONVO?",
              confirmBtnText: "Activar"
            },
            onClick: (id: string, item: TenantDataType) => { activeOnvo(item) }
          },
          {
            isVisible: (item: TenantDataType) => !!item.onvoSubscriptionId,
            actionTitle: "Desactivar Suscripcion Onvo",
            iconProps: { icon: faMoneyBillTransfer, className: styles.activeBtn },
            needConfirm: true,
            confirmProps: {
              title: "Desactivar Suscription Onvo",
              confirmMessage: "¿Seguro que desea desactivar la suscripcion recurrente de ONVO?",
              confirmBtnText: "Desactivar"
            },
            onClick: (id: string, item: TenantDataType) => { deactiveOnvo(item) }
          },
          {
            actionTitle: "Eliminar Suscripcion",
            iconProps: { icon: faTrash, className: styles.cancelBtn },
            onClick: (_id: string, item: TenantDataType) => { setTenantToDelete(item) }
          },
        ]}
      >
      </ModelIndex>
      <ConfirmModal
        isOpen={!!tenantToDelete}
        toggle={() => setTenantToDelete(null)}
        title="Eliminar Suscripcion"
        primaryBtn="Eliminar Definitivamente"
        primaryBtnColor="danger"
        secondaryBtn="Cancelar"
        secondaryBtnColor="secondary"
        onConfirm={deleteTenant}
        onCancel={() => setTenantToDelete(null)}
      >
        <p>
          <strong>¡ADVERTENCIA!</strong> Esta accion es <strong>IRREVERSIBLE</strong>.
        </p>
        <p>
          Se eliminaran <strong>todos los datos</strong> de la suscripcion:
        </p>
        <p>
          <strong>{tenantToDelete?.nombre}</strong>
        </p>
        <p>
          Facturas, clientes, productos, proveedores, cotizaciones y todos los registros
          asociados seran eliminados permanentemente. Los usuarios de Cognito tambien seran borrados.
        </p>
      </ConfirmModal>
      <LoadingScreen isLoading={isLoading} />
    </PageContainer >
  );
}

export default TenantIndex;
